import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  Clock,
  Building2,
  CalendarPlus,
  Phone,
  Mail,
  ChevronRight,
  Timer,
  CheckCircle2,
  XCircle,
  Loader2,
  UserCheck,
  History,
  Send,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { formatDate } from "@/lib/demo-data";
import { toast } from "sonner";

const CONTACT_METHODS = [
  { value: "PHONE", label: "Téléphone" },
  { value: "EMAIL", label: "Email" },
  { value: "MEETING", label: "Réunion" },
  { value: "CHAT", label: "Chat" },
  { value: "OTHER", label: "Autre" },
] as const;

type Segment = "all" | "j7" | "j3" | "j0" | "j7plus" | "active";

const SEGMENT_CONFIG: Record<
  Exclude<Segment, "all" | "active">,
  { label: string; color: string; bgClass: string; textClass: string }
> = {
  j7: {
    label: "J-7",
    color: "amber",
    bgClass: "bg-amber-500/10 border-amber-200",
    textClass: "text-amber-700",
  },
  j3: {
    label: "J-3",
    color: "orange",
    bgClass: "bg-orange-500/10 border-orange-200",
    textClass: "text-orange-700",
  },
  j0: {
    label: "Expiré",
    color: "red",
    bgClass: "bg-destructive/10 border-destructive/20",
    textClass: "text-destructive",
  },
  j7plus: {
    label: "Expiré +7j",
    color: "gray",
    bgClass: "bg-muted border-muted-foreground/20",
    textClass: "text-muted-foreground",
  },
};

function getTrialSegment(
  planExpiresAt: string | null,
  status: string
): Exclude<Segment, "all"> | null {
  if (!planExpiresAt) return null;
  if (status !== "TRIAL") return null;

  const now = new Date();
  const expires = new Date(planExpiresAt);
  const diffMs = expires.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < -7) return "j7plus";
  if (diffDays <= 0) return "j0";
  if (diffDays <= 3) return "j3";
  if (diffDays <= 7) return "j7";
  return "active";
}

export default function TrialAlerts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";
  const [segment, setSegment] = useState<Segment>("all");
  const [extendDialog, setExtendDialog] = useState<{
    companyId: string;
    companyName: string;
    currentExpiry: string;
  } | null>(null);
  const [extendDays, setExtendDays] = useState("7");

  const [contactDialog, setContactDialog] = useState<{
    companyId: string;
    companyName: string;
  } | null>(null);
  const [contactMethod, setContactMethod] = useState<string>("PHONE");
  const [contactNote, setContactNote] = useState("");

  const [historyDialog, setHistoryDialog] = useState<{
    companyId: string;
    companyName: string;
  } | null>(null);

  const [relanceDialog, setRelanceDialog] = useState<{
    companyId: string;
    companyName: string;
  } | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [sortKey, setSortKey] = useState<"expiry" | "usage">("expiry");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Bascule la colonne triée ; un clic sur la colonne déjà active inverse le sens.
  function toggleSort(key: "expiry" | "usage") {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // usage = priorité outreach → fort en premier (desc) ; expiry → urgent (asc).
      setSortDir(key === "usage" ? "desc" : "asc");
    }
  }

  // Source: alertes Trial (TrialAlert) — réutilise les données émises par
  // trialAlertScheduler (J-7/J-3/J-0). getTrialAlerts renvoie planExpiresAt,
  // plan et le contact, contrairement à admin.companies.list qui les omet.
  const { data: trialAlertsData, isLoading } =
    trpc.admin.trialAlerts.getTrialAlerts.useQuery(undefined, {
      refetchInterval: 60_000,
    });

  const utils = trpc.useUtils();

  const updateMutation = trpc.admin.companies.update.useMutation({
    onSuccess: () => {
      toast.success("Trial étendu avec succès");
      setExtendDialog(null);
      setExtendDays("7");
      utils.admin.trialAlerts.getTrialAlerts.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'extension"),
  });

  const markContactedMutation = trpc.admin.trialAlerts.markAsContacted.useMutation({
    onSuccess: () => {
      toast.success("Contact enregistré — activité Sales créée");
      setContactDialog(null);
      setContactMethod("PHONE");
      setContactNote("");
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'enregistrement"),
  });

  const { data: alertHistory, isLoading: historyLoading } =
    trpc.admin.trialAlerts.getAlertHistory.useQuery(
      { companyId: historyDialog?.companyId ?? "", limit: 20 },
      { enabled: !!historyDialog }
    );

  const { data: emailTemplates } = trpc.admin.trialAlerts.listTemplates.useQuery(
    undefined,
    { enabled: !!relanceDialog }
  );

  const sendRelanceMutation = trpc.admin.trialAlerts.sendRelanceEmail.useMutation({
    onSuccess: (data) => {
      toast.success(`Email de relance envoyé à ${data.sentTo}`);
      setRelanceDialog(null);
      setSelectedTemplateId("");
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'envoi"),
  });

  // AOP-8518 — assignation OPS pour le cold outreach J+12. ADMIN_OPS ne peut pas
  // lister tout le staff (admin.staff.list = SUPER_ADMIN), donc l'OPS connecté
  // s'assigne lui-même ("M'assigner") ou désassigne ; assignedOpsUserId = user.id.
  const assignOpsMutation = trpc.admin.trialAlerts.assignOps.useMutation({
    onSuccess: () => {
      utils.admin.trialAlerts.getTrialAlerts.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'assignation"),
  });

  function assignToMe(companyId: string) {
    if (!user?.id) return;
    assignOpsMutation.mutate(
      { companyId, assignedOpsUserId: user.id },
      { onSuccess: () => toast.success("Trial assigné — à vous le cold outreach") }
    );
  }

  function unassignOps(companyId: string) {
    assignOpsMutation.mutate(
      { companyId, assignedOpsUserId: null },
      { onSuccess: () => toast.success("Trial désassigné") }
    );
  }

  function opsName(ops: { firstName?: string | null; lastName?: string | null; email?: string | null } | null): string {
    if (!ops) return "Non assigné";
    const full = [ops.firstName, ops.lastName].filter(Boolean).join(" ").trim();
    return full || ops.email || "Assigné";
  }

  // getTrialAlerts renvoie une ligne par (company, type d'alerte) avec
  // @@unique([companyId, type]) → jusqu'à 3 alertes par entreprise. On déduplique
  // par entreprise en gardant l'alerte la plus sévère.
  const companies: any[] = useMemo(() => {
    const severityRank: Record<string, number> = {
      EXPIRED: 3,
      CRITICAL: 2,
      WARNING: 1,
    };
    const byCompany = new Map<string, any>();
    for (const a of trialAlertsData ?? []) {
      const existing = byCompany.get(a.companyId);
      if (
        !existing ||
        (severityRank[a.severity] ?? 0) > (severityRank[existing.severity] ?? 0)
      ) {
        byCompany.set(a.companyId, {
          id: a.company.id,
          name: a.company.name,
          status: a.company.status,
          planExpiresAt: a.company.planExpiresAt,
          plan: a.company.plan,
          email: a.company.contact?.email ?? null,
          phone: a.company.contact?.phone ?? null,
          siret: null,
          severity: a.severity,
          // usage_score & OPS assigné — livrés par admin.trialAlerts.getTrialAlerts
          // (AOP-8534). usageScore = somme pondérée devis/factures/clients/sites ;
          // assignedOps = staff résolu ({id,email,firstName,lastName}) ou null.
          usageScore: a.usageScore ?? 0,
          assignedOps: a.assignedOps ?? null,
        });
      }
    }
    return [...byCompany.values()];
  }, [trialAlertsData]);

  const segmented = useMemo(() => {
    const result: Record<Exclude<Segment, "all">, any[]> = {
      j7: [],
      j3: [],
      j0: [],
      j7plus: [],
      active: [],
    };

    for (const c of companies) {
      const seg = getTrialSegment(c.planExpiresAt, c.status);
      if (seg) result[seg].push(c);
    }

    return result;
  }, [companies]);

  const counts = {
    all: companies.length,
    j7: segmented.j7.length,
    j3: segmented.j3.length,
    j0: segmented.j0.length,
    j7plus: segmented.j7plus.length,
    active: segmented.active.length,
  };

  const displayedCompanies = useMemo(() => {
    const list =
      segment === "all" ? [...companies] : [...(segmented[segment] ?? [])];
    return list.sort((a: any, b: any) => {
      if (sortKey === "usage") {
        const aU = a.usageScore ?? 0;
        const bU = b.usageScore ?? 0;
        return sortDir === "asc" ? aU - bU : bU - aU;
      }
      const aExp = a.planExpiresAt
        ? new Date(a.planExpiresAt).getTime()
        : Infinity;
      const bExp = b.planExpiresAt
        ? new Date(b.planExpiresAt).getTime()
        : Infinity;
      return sortDir === "asc" ? aExp - bExp : bExp - aExp;
    });
  }, [segment, companies, segmented, sortKey, sortDir]);

  function handleExtendTrial() {
    if (!extendDialog) return;
    const days = parseInt(extendDays, 10);
    if (isNaN(days) || days < 1 || days > 30) return;

    const currentExpiry = new Date(extendDialog.currentExpiry);
    const newExpiry = new Date(
      Math.max(currentExpiry.getTime(), Date.now()) + days * 86400000
    );

    updateMutation.mutate({
      id: extendDialog.companyId,
      planExpiresAt: newExpiry.toISOString(),
      reason: `Extension trial de ${days} jours via alertes trials`,
    } as any);
  }

  function handleMarkContacted() {
    if (!contactDialog) return;
    markContactedMutation.mutate({
      companyId: contactDialog.companyId,
      method: contactMethod as "PHONE" | "EMAIL" | "MEETING" | "CHAT" | "OTHER",
      note: contactNote || undefined,
    });
  }

  function handleSendRelance() {
    if (!relanceDialog || !selectedTemplateId) return;
    sendRelanceMutation.mutate({
      companyId: relanceDialog.companyId,
      templateId: selectedTemplateId,
    });
  }

  function daysUntilExpiry(planExpiresAt: string | null): string {
    if (!planExpiresAt) return "—";
    const diff = Math.ceil(
      (new Date(planExpiresAt).getTime() - Date.now()) / 86400000
    );
    if (diff < 0) return `Expiré il y a ${Math.abs(diff)}j`;
    if (diff === 0) return "Expire aujourd'hui";
    return `${diff}j restants`;
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des trials…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Trials en cours</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Suivi des entreprises en période d'essai — cold outreach vers J+12
            avant expiration sans upgrade. Triable par jours restants ou score
            d'usage ; assignez-vous un trial pour piloter la relance.
          </p>
        </div>

        {/* Segment cards */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          <Card
            className={`cursor-pointer transition-all ${segment === "all" ? "ring-2 ring-primary" : "hover:bg-muted/50"}`}
            onClick={() => setSegment("all")}
          >
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold">{counts.all}</div>
              <div className="text-xs text-muted-foreground">Tous</div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all ${segment === "active" ? "ring-2 ring-green-500" : "hover:bg-muted/50"} ${counts.active > 0 ? "bg-green-500/5 border-green-200" : ""}`}
            onClick={() => setSegment("active")}
          >
            <CardContent className="py-4 text-center">
              <div className={`text-2xl font-bold ${counts.active > 0 ? "text-green-700" : ""}`}>
                {counts.active}
              </div>
              <div className="text-xs text-muted-foreground">Actifs</div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all ${segment === "j7" ? "ring-2 ring-amber-500" : "hover:bg-muted/50"} ${counts.j7 > 0 ? SEGMENT_CONFIG.j7.bgClass : ""}`}
            onClick={() => setSegment("j7")}
          >
            <CardContent className="py-4 text-center">
              <div className={`text-2xl font-bold ${counts.j7 > 0 ? SEGMENT_CONFIG.j7.textClass : ""}`}>
                {counts.j7}
              </div>
              <div className="text-xs text-muted-foreground">J-7</div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all ${segment === "j3" ? "ring-2 ring-orange-500" : "hover:bg-muted/50"} ${counts.j3 > 0 ? SEGMENT_CONFIG.j3.bgClass : ""}`}
            onClick={() => setSegment("j3")}
          >
            <CardContent className="py-4 text-center">
              <div className={`text-2xl font-bold ${counts.j3 > 0 ? SEGMENT_CONFIG.j3.textClass : ""}`}>
                {counts.j3}
              </div>
              <div className="text-xs text-muted-foreground">J-3</div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all ${segment === "j0" ? "ring-2 ring-destructive" : "hover:bg-muted/50"} ${counts.j0 > 0 ? SEGMENT_CONFIG.j0.bgClass : ""}`}
            onClick={() => setSegment("j0")}
          >
            <CardContent className="py-4 text-center">
              <div className={`text-2xl font-bold ${counts.j0 > 0 ? SEGMENT_CONFIG.j0.textClass : ""}`}>
                {counts.j0}
              </div>
              <div className="text-xs text-muted-foreground">Expiré</div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all ${segment === "j7plus" ? "ring-2 ring-muted-foreground" : "hover:bg-muted/50"} ${counts.j7plus > 0 ? SEGMENT_CONFIG.j7plus.bgClass : ""}`}
            onClick={() => setSegment("j7plus")}
          >
            <CardContent className="py-4 text-center">
              <div className={`text-2xl font-bold ${counts.j7plus > 0 ? SEGMENT_CONFIG.j7plus.textClass : ""}`}>
                {counts.j7plus}
              </div>
              <div className="text-xs text-muted-foreground">Expiré +7j</div>
            </CardContent>
          </Card>
        </div>

        {/* Urgent alerts banner */}
        {(counts.j0 > 0 || counts.j3 > 0) && (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
                <div>
                  <div className="text-sm font-medium">
                    {counts.j0 + counts.j3} trial(s) nécessitent une action urgente
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {counts.j0 > 0 && `${counts.j0} expiré(s)`}
                    {counts.j0 > 0 && counts.j3 > 0 && " — "}
                    {counts.j3 > 0 && `${counts.j3} expire(nt) dans 3 jours`}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trials table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Timer className="h-4 w-4" />
              {segment === "all"
                ? "Tous les trials"
                : segment === "active"
                  ? "Trials actifs (>7j)"
                  : `Trials ${SEGMENT_CONFIG[segment]?.label ?? segment}`}
              <Badge variant="secondary" className="text-xs ml-auto">
                {displayedCompanies.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {displayedCompanies.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                Aucun trial dans ce segment
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Entreprise</TableHead>
                    <TableHead className="text-xs">Contact</TableHead>
                    <TableHead className="text-xs">Plan</TableHead>
                    <TableHead className="text-xs">
                      <button
                        type="button"
                        className={`flex items-center gap-1 hover:text-foreground ${sortKey === "expiry" ? "text-foreground font-medium" : ""}`}
                        onClick={() => toggleSort("expiry")}
                        title="Trier par jours restants"
                      >
                        Jours restants
                        {sortKey === "expiry" &&
                          (sortDir === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="text-xs">
                      <button
                        type="button"
                        className={`flex items-center gap-1 hover:text-foreground ${sortKey === "usage" ? "text-foreground font-medium" : ""}`}
                        onClick={() => toggleSort("usage")}
                        title="Trier par score d'usage — priorité cold outreach"
                      >
                        Score d'usage
                        {sortKey === "usage" &&
                          (sortDir === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          ))}
                      </button>
                    </TableHead>
                    <TableHead className="text-xs">OPS assigné</TableHead>
                    <TableHead className="text-xs">Statut</TableHead>
                    <TableHead className="text-xs w-[200px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedCompanies.map((company: any) => {
                    const seg = getTrialSegment(
                      company.planExpiresAt,
                      company.status
                    );
                    const segCfg =
                      seg && seg !== "active"
                        ? SEGMENT_CONFIG[seg]
                        : null;

                    return (
                      <TableRow
                        key={company.id}
                        className={
                          seg === "j0" || seg === "j7plus"
                            ? "bg-destructive/5"
                            : seg === "j3"
                              ? "bg-orange-500/5"
                              : ""
                        }
                      >
                        <TableCell>
                          <button
                            className="flex items-center gap-2 hover:underline text-left"
                            onClick={() =>
                              navigate(`/companies/${company.id}`)
                            }
                          >
                            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div>
                              <div className="text-sm font-medium">
                                {company.name}
                              </div>
                              {company.siret && (
                                <div className="text-xs text-muted-foreground">
                                  {company.siret}
                                </div>
                              )}
                            </div>
                          </button>
                        </TableCell>
                        <TableCell className="text-xs">
                          {company.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              <span className="truncate max-w-[150px]">
                                {company.email}
                              </span>
                            </div>
                          )}
                          {company.phone && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {company.phone}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {company.plan ?? "DECOUVERTE"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            {company.planExpiresAt
                              ? formatDate(company.planExpiresAt)
                              : "—"}
                          </div>
                          <div
                            className={`text-xs font-medium ${
                              segCfg ? segCfg.textClass : "text-muted-foreground"
                            }`}
                          >
                            {daysUntilExpiry(company.planExpiresAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-xs font-medium ${
                              company.usageScore > 0
                                ? "text-foreground"
                                : "text-muted-foreground"
                            }`}
                            title="Somme pondérée devis/factures/clients/sites depuis l'ouverture du compte"
                          >
                            {company.usageScore ?? 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          {company.assignedOps ? (
                            <div className="flex items-center gap-1">
                              <Badge variant="secondary" className="text-xs gap-1">
                                <UserCheck className="h-3 w-3" />
                                {opsName(company.assignedOps)}
                              </Badge>
                              {canAct &&
                                (isSuperAdmin ||
                                  company.assignedOps.id === user?.id) && (
                                  <button
                                    type="button"
                                    className="text-muted-foreground hover:text-destructive"
                                    title="Désassigner"
                                    disabled={assignOpsMutation.isPending}
                                    onClick={() => unassignOps(company.id)}
                                  >
                                    <XCircle className="h-3.5 w-3.5" />
                                  </button>
                                )}
                            </div>
                          ) : canAct ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs px-2"
                              disabled={assignOpsMutation.isPending}
                              onClick={() => assignToMe(company.id)}
                            >
                              M'assigner
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Non assigné
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {segCfg ? (
                            <Badge
                              className={`text-xs ${segCfg.bgClass} ${segCfg.textClass}`}
                            >
                              {segCfg.label}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              Actif
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            {canAct && company.email && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                asChild
                              >
                                <a href={`mailto:${company.email}?subject=${encodeURIComponent(`Votre période d'essai Constrigo — ${company.name}`)}`}>
                                  <Mail className="h-3 w-3 mr-1" />
                                  Email
                                </a>
                              </Button>
                            )}
                            {canAct && company.phone && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                asChild
                              >
                                <a href={`tel:${company.phone}`}>
                                  <Phone className="h-3 w-3 mr-1" />
                                  Appeler
                                </a>
                              </Button>
                            )}
                            {canAct && company.planExpiresAt && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() =>
                                  setExtendDialog({
                                    companyId: company.id,
                                    companyName: company.name,
                                    currentExpiry: company.planExpiresAt,
                                  })
                                }
                              >
                                <CalendarPlus className="h-3 w-3 mr-1" />
                                Étendre
                              </Button>
                            )}
                            {canAct && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() =>
                                  setContactDialog({
                                    companyId: company.id,
                                    companyName: company.name,
                                  })
                                }
                              >
                                <UserCheck className="h-3 w-3 mr-1" />
                                Contacté
                              </Button>
                            )}
                            {canAct && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-blue-700"
                                onClick={() => {
                                  setRelanceDialog({
                                    companyId: company.id,
                                    companyName: company.name,
                                  });
                                  setSelectedTemplateId("");
                                }}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Relance
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() =>
                                setHistoryDialog({
                                  companyId: company.id,
                                  companyName: company.name,
                                })
                              }
                            >
                              <History className="h-3 w-3 mr-1" />
                              Historique
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() =>
                                navigate(`/companies/${company.id}`)
                              }
                            >
                              Voir
                              <ChevronRight className="h-3 w-3 ml-0.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Extend trial dialog ─── */}
      <Dialog
        open={!!extendDialog}
        onOpenChange={(open) => {
          if (!open) setExtendDialog(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5" />
              Étendre le trial
            </DialogTitle>
            <DialogDescription>
              {extendDialog?.companyName} — expire le{" "}
              {extendDialog?.currentExpiry
                ? formatDate(extendDialog.currentExpiry)
                : "—"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Durée d'extension (jours)
              </label>
              <Select value={extendDays} onValueChange={setExtendDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">+3 jours</SelectItem>
                  <SelectItem value="7">+7 jours</SelectItem>
                  <SelectItem value="14">+14 jours</SelectItem>
                  <SelectItem value="30">+30 jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {extendDialog && (
              <div className="text-xs text-muted-foreground">
                Nouvelle expiration :{" "}
                {formatDate(
                  new Date(
                    Math.max(
                      new Date(extendDialog.currentExpiry).getTime(),
                      Date.now()
                    ) +
                      parseInt(extendDays) * 86400000
                  ).toISOString()
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExtendDialog(null)}
            >
              Annuler
            </Button>
            <Button
              disabled={updateMutation.isPending}
              onClick={handleExtendTrial}
            >
              {updateMutation.isPending ? "Extension…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ─── Mark as contacted dialog ─── */}
      <Dialog
        open={!!contactDialog}
        onOpenChange={(open) => {
          if (!open) {
            setContactDialog(null);
            setContactMethod("PHONE");
            setContactNote("");
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Marquer contacté
            </DialogTitle>
            <DialogDescription>
              {contactDialog?.companyName} — enregistrer un contact commercial
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Méthode de contact
              </label>
              <Select value={contactMethod} onValueChange={setContactMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Note <span className="text-muted-foreground font-normal">(optionnel)</span>
              </label>
              <Textarea
                value={contactNote}
                onChange={(e) => setContactNote(e.target.value)}
                placeholder="Résumé de l'échange, prochaine étape..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialog(null)}>
              Annuler
            </Button>
            <Button
              disabled={markContactedMutation.isPending}
              onClick={handleMarkContacted}
            >
              {markContactedMutation.isPending ? "Enregistrement…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Send relance email dialog ─── */}
      <Dialog
        open={!!relanceDialog}
        onOpenChange={(open) => {
          if (!open) {
            setRelanceDialog(null);
            setSelectedTemplateId("");
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Envoyer email de relance
            </DialogTitle>
            <DialogDescription>
              {relanceDialog?.companyName} — choisissez un template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!emailTemplates?.length ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                Aucun template de relance trial disponible.
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium">Template email</label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un template" />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRelanceDialog(null)}>
              Annuler
            </Button>
            <Button
              disabled={!selectedTemplateId || sendRelanceMutation.isPending}
              onClick={handleSendRelance}
            >
              {sendRelanceMutation.isPending ? "Envoi…" : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Alert history dialog ─── */}
      <Dialog
        open={!!historyDialog}
        onOpenChange={(open) => {
          if (!open) setHistoryDialog(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historique alertes — {historyDialog?.companyName}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement…
              </div>
            ) : !alertHistory?.items?.length ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Aucune alerte enregistrée pour cette entreprise
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Expiration plan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alertHistory.items.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs">
                        {formatDate(log.sentAt)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {log.alertType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatDate(log.planExpiresAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialog(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
